import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styled from '@emotion/styled';
import { init, identify, Identify } from '@amplitude/analytics-browser';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Workbox } from 'workbox-window';
import {
  loadChurnZeroScript,
  initChurnZero,
  trackCZEvent,
  setCZAttribute,
  setCZModule,
} from '../utils/churnZeroHelper';

import { ThemeProvider } from 'emotion-theming';
import { useSelector, useDispatch } from 'react-redux';
import { CLEAR_LOGIN_METHOD } from '../../modules/login/ducks/actionTypes';
import ErrorBoundaryWrapper from './ErrorBoundaryWrapper';

import AppRoutes from '../../services/Routes/routes';
import mainTheme from '../theme/theme';
import FourOhFourRoute from '../../services/Routes/FourOhFourRoute';

const AppWrapper = ({ flags, ldClient }) => {
  const isAmplitudeInitialized = useRef(false);
  const isChurnZeroInitialized = useRef(false);
  const isContentSquareLoaded = useRef(false);
  const reasonRef = useRef(null);
  const currentUserRef = useRef(null);

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const loginMethod = useSelector((state) => state.auth.data.loginMethod);
  const showMaintenanceModeAlert = useSelector((state) => state.auth.ui.showMaintenanceModeAlert);
  const pathname = useSelector((state) => state.router.location.pathname);
  const isCalendarView = pathname?.startsWith('/build') || pathname?.startsWith('/calendar');
  const skin = currentUser?.userSkin !== undefined && currentUser?.userSkin;

  // Keep currentUserRef in sync so the SW effect can read the latest value
  // without needing to re-register the service worker on every currentUser change
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // START service worker new version setup
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const wb = new Workbox('/service-worker.js');

    const showSkipWaitingPrompt = () => {
      // Once the new SW takes control, reload to run under the new version
      wb.addEventListener('controlling', () => {
        window.location.reload();
      });

      // Only show the toast when a user is logged in
      if (currentUserRef.current?.id) {
        const VersionUpdateContent = ({ closeToast }) => (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: '#ffffff',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
            fontFamily: 'Nunito Sans',
          }}
          >
            <span style={{ fontWeight: 600 }}>
              A newer version of TeamBuildr is available
            </span>
            <button
              type='button'
              onClick={() => {
                reasonRef.current = 'refresh';
                closeToast();
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#4A90E2',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: '16px',
                padding: 0,
                fontFamily: 'Nunito Sans',
              }}
            >
              REFRESH
            </button>
          </div>
        );

        toast.info(
          ({ closeToast }) => <VersionUpdateContent closeToast={closeToast} />,
          {
            autoClose: false,
            closeOnClick: false,
            hideProgressBar: true,
            onClose: () => {
              if (reasonRef.current === 'refresh') {
                wb.messageSkipWaiting();
              }
              reasonRef.current = null;
            },
            position: 'top-right',
            toastId: 'version-update',
          },
        );
      }
    };

    wb.addEventListener('waiting', showSkipWaitingPrompt);
    wb.register();

    // Heartbeat: re-check /service-worker.js on an interval and when the tab
    // regains focus so long-lived sessions notice new builds without a manual
    // refresh. wb.update() is a no-op when the SW file is byte-identical.
    const checkForUpdate = () => wb.update();
    const intervalId = setInterval(checkForUpdate, 15 * 60 * 1000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // END service worker new version setup

  // START Amplitude setup
  const identifyAmplitudeUser = () => {
    if (!currentUser?.id) return;

    // Create an Identify instance to set Amplitude user properties
    const identifyUser = new Identify();

    // Set multiple user properties
    identifyUser.set('admin', currentUser?.admin);
    identifyUser.set('gender', currentUser?.gender);
    identifyUser.set('organizationAccessLevel', currentUser?.organizationAccessLevel);
    identifyUser.set('organizationId', currentUser?.organizationId);
    identifyUser.set('organizationName', currentUser?.organizationName);
    identifyUser.set('organizationResellerAccountEnabled', currentUser?.organizationResellerAccountEnabled);
    identifyUser.set('organizationStripeSubscriptionActive', currentUser?.organizationStripeSubscriptionActive);
    identifyUser.set('organizationSubscriptionStatus', currentUser?.organizationSubscriptionStatus);
    identifyUser.set('primaryAdmin', currentUser?.primaryAdmin);
    identifyUser.set('ssHubEnabled', currentUser?.userOrgSSHubSettings?.ssHubEnabled);

    // Set the Amplitude user properties
    identify(identifyUser);
  };

  const initializeAmplitude = (isClientAlreadyInitialized) => {
    const isAmplitudeFlagEnabled = flags && flags.webAmplitude;

    if (isAmplitudeFlagEnabled) {
      if (!isClientAlreadyInitialized) {
        // Initialize Amplitude
        init('153704185f16943e3f8609c338cdb899', {
          defaultTracking: true,
          minIdLength: 1,
          userId: currentUser?.id,
        });

        isAmplitudeInitialized.current = true;
      }

      identifyAmplitudeUser();
    }
  };

  useEffect(() => {
    // Only allow Amplitude to initialize in production and after a user is logged in.
    if (process.env.ENVIRONMENT === 'PRODUCTION' && currentUser?.id && flags) {
      initializeAmplitude(isAmplitudeInitialized.current);
    }
  }, [currentUser, flags]);
  // END Amplitude setup

  // START ChurnZero setup
  const mapPathnameToModule = (path) => {
    if (path.startsWith('/calendar') || path.startsWith('/build')) return 'Calendar';
    if (path.startsWith('/reporting')) return 'Reporting';
    if (path.startsWith('/conversations')) return 'Conversations';
    if (path.startsWith('/documents')) return 'Documents';
    if (path.startsWith('/leaderboard')) return 'Leaderboard';
    if (path.startsWith('/settings')) return 'Settings';
    if (path.startsWith('/wearables')) return 'Wearables';
    if (path.startsWith('/habit-tracking')) return 'Habit Tracking';
    if (path.startsWith('/sports-science')) return 'Sports Science';
    if (path.startsWith('/weightroom')) return 'Weightroom';
    if (path.startsWith('/daily-brief')) return 'Daily Brief';
    if (path.startsWith('/dashboard')) return 'Home';
    return 'Other';
  };

  const initializeChurnZero = (isClientAlreadyInitialized) => {
    const isChurnZeroFlagEnabled = flags && flags.webChurnZero;

    if (isChurnZeroFlagEnabled) {
      if (!isClientAlreadyInitialized) {
        loadChurnZeroScript();
        initChurnZero(
          process.env.CHURNZERO_APP_KEY,
          currentUser.accountCode,
          currentUser.email,
        );

        setCZAttribute('contact', {
          FirstName: currentUser.first,
          LastName: currentUser.last,
          Email: currentUser.email,
        });

        if (loginMethod) {
          trackCZEvent('Login', loginMethod);
          dispatch({ type: CLEAR_LOGIN_METHOD });
        }

        isChurnZeroInitialized.current = true;
      }
    }
  };

  useEffect(() => {
    if (currentUser?.id && currentUser?.admin === 1 && currentUser?.organizationSubscriptionStatus === 'ACTIVE' && flags && process.env.CHURNZERO_APP_KEY) {
      initializeChurnZero(isChurnZeroInitialized.current);
    }
  }, [currentUser, flags]);

  useEffect(() => {
    if (isChurnZeroInitialized.current && pathname) {
      setCZModule(mapPathnameToModule(pathname));
    }
  }, [pathname]);
  // END ChurnZero setup

  const theme = {
    colorAccent: skin && skin.accent,
    colorPrimary: skin && skin.primary,
    colorSecondary: skin && skin.secondary,
    isDarkMode: skin && skin.darknessMode,
    isAccentDarkTheme: skin && skin.accentDarkTheme,
    isPrimaryDarkTheme: skin && skin.darkTheme,
    isSecondaryDarkTheme: skin && skin.secondaryDarkTheme,
    ...mainTheme,
  };

  const StyledToastContainer = styled(ToastContainer)`
    &&&.Toastify__toast-container {
      padding: 20px;
      margin-top: ${(props) => (props.isCalendarView ? '65px' : '15px')};
      min-width: 375px;
    }
    .Toastify__toast {
      background: transparent;
      box-shadow: none;
      border-radius: 4px;
      padding: 0;
      min-height: auto;
      margin-bottom: 0;
    }
    .Toastify__toast-body {
      font-family: 'Nunito Sans';
      color: #333;
      padding: 0;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .Toastify__toast--success,
    .Toastify__toast--error,
    .Toastify__toast--warning,
    .Toastify__toast--info {
      background: transparent;
      padding: 20px;
    }
    .Toastify__toast-icon {
      display: none;
    }
    .Toastify__progress-bar {
      bottom: 20px;
      left: 20px;
      width: calc(100% - 40px);
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
      height: 4px;
    }
    .Toastify__progress-bar--error {
      background: red;
    }
    .Toastify__progress-bar--success,
    .Toastify__progress-bar--info,
    .Toastify__progress-bar--warning {
      background: #4A90E2;
    }
    .Toastify__close-button {
      position: absolute;
      right: auto;
      left: 8px;
      top: 8px;
      background: white;
      border: 2px solid #B0B0B0;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
      padding: 0;
    }
    .Toastify__close-button > svg {
      width: 12px;
      height: 12px;
      color: #B0B0B0;
      fill: #B0B0B0;
      margin-left: 2px;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(10px);
        max-height: 0;
      }
      to {
        opacity: 1;
        transform: translateY(0);
        max-height: 200px;
      }
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
        max-height: 0;
      }
      to {
        opacity: 1;
        transform: translateY(0);
        max-height: 200px;
      }
    }
  `;

  useEffect(() => {
    if (showMaintenanceModeAlert) {
      // TODO: potentially create a nicer banner component to show this message
      const alertMessage = 'TeamBuildr is currently under maintenance. Please visit https://status.teambuildr.com to check the current status of TeamBuildr servers.';
      alert(alertMessage);
    }
  }, [showMaintenanceModeAlert]);

  useEffect(() => {
    if (currentUser && Object.keys(currentUser).length && ldClient) {
      ldClient.identify({
        kind: 'user',
        key: currentUser.id,
        name: `${currentUser.first} ${currentUser.last}`,
        email: currentUser.email,
        accountCode: currentUser.accountCode,
        organizationSubscriptionId: currentUser.organizationSubscriptionId,
        platform: 'web',
      });
    }
  }, [currentUser, ldClient]);

  // Load ContentSquare script for trial admins
  useEffect(() => {
    if (currentUser?.organizationSubscriptionStatus === 'IN_TRIAL' && currentUser?.admin === 1 && !isContentSquareLoaded.current) {
      // Add ContentSquare tracking script
      const contentSquareScript = document.createElement('script');
      contentSquareScript.src = 'https://t.contentsquare.net/uxa/91be1269c2b06.js';
      contentSquareScript.async = true;
      document.head.appendChild(contentSquareScript);

      console.log('ContentSquare script loaded');
      // Set the useRef to true to prevent re-initialization
      isContentSquareLoaded.current = true;
    }
    console.log('ContentSquare script not loaded');
  }, [currentUser]);

  return (
    <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID}>
      <ThemeProvider theme={theme}>
        <ErrorBoundaryWrapper fallback={<FourOhFourRoute errorBoundary />}>
          <div className='custom-toast-container' style={{ position: 'absolute' }}>
            <StyledToastContainer
              position='top-right'
              autoClose={5000}
              closeOnClick
              pauseOnHover
              hideProgressBar={false}
              closeButton
              draggable={false}
              theme='light'
              isCalendarView={isCalendarView}
            />
          </div>
          <AppRoutes />
        </ErrorBoundaryWrapper>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

AppWrapper.propTypes = {
  flags: PropTypes.shape({
    webAmplitude: PropTypes.bool,
    webChurnZero: PropTypes.bool,
  }),
  ldClient: PropTypes.shape({
    identify: PropTypes.func,
  }),
};

AppWrapper.defaultProps = {
  flags: {},
  ldClient: null,
};

export default withLDConsumer()(AppWrapper);
